import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import ButtonGroup from '../../../../../../components/ButtonGroup/ButtonGroup';
import { toastService } from '../../../../../../utils/toastService';
import { useNavigate } from 'react-router-dom';
import { useGetChassisListForCVRFQuery, useCheckChassisNoQuery, useGetByIdVehicleRegistrationFormQuery, useDeleteVehicleRegistrationFormMutation, useChangeStatusVehicleRegistrationFormMutation } from '../../../../../../api';
import CountryDropdown from '../../../../../../components/common/CountryDropdown';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { formatDateForApi } from '../../../../../../utils/dateUtils';
import { selectUserRoles } from '../../../../../../features/auth/authSlice';
import { Roles } from '../../../../../../utils/roleEnums';
import ApprovalCommentsTable from '../../component/ApprovalCommentsTable';
import ChassisDropdown from '../../../shared/components/ChassisDropdown';
import DateChangeRequestField from '../../../shared/components/DateChangeRequestField';
import CVRFCommentConfirmDialog from './CVRFCommentConfirmDialog';
import { validateEmail, validatePhone, validatePostalCode, getMissingRequiredFields } from '../utils/cvrfValidators';
import { FORM_TYPE, DATE_FIELD } from '../../../../../../utils/formDateChangeConstants';

const CVRF_LIST_ROUTE = '/warranty-management/warranty-process-forms/dealer-vehicle-registration-form';

const FormVehicleRegistrationForm = ({ CVRFStatus, editMode, id, submitted, formData, handleChange, saveVehicleRegistrationForm, resetForm, setSubmitted, setFormData }) => {
    const navigate = useNavigate();
    const { th } = useLanguage();
    const userRoles = useSelector(selectUserRoles);
    const authorizationCheckforVehicleRegistrationForm =
        Array.isArray(userRoles) &&
        (userRoles.includes(Roles.WARRANTY_TEAM) ||
            userRoles.includes(Roles.WARRANTY_MANAGER) ||
            userRoles.includes(Roles.SSH_MANAGER) ||
            userRoles.includes(Roles.SSH_DIRECTOR) ||
            userRoles.includes(Roles.SSH_GENERAL_MANAGER) ||
            userRoles.includes(Roles.CEO) ||
            userRoles.includes(Roles.ADMIN));

    const [initialFormData, setInitialFormData] = useState({});

    const isFormEditable = formData?.status !== CVRFStatus.Approved && formData?.status !== CVRFStatus.SentForApproval;

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [showReturnDialog, setShowReturnDialog] = useState(false);
    const [returnMessage, setReturnMessage] = useState('');

     const [showSendForApprovalDialog, setShowSendForApprovalDialog] = useState(false);
    const [sendForApprovalMessage, setSendForApprovalMessage] = useState('');

    const [showApproveDialog, setShowApproveDialog] = useState(false);
    const [approveMessage, setApproveMessage] = useState('');

    const { data: vehicleRegistrationFormData, isLoading: isLoadingVehicleRegistrationForm } = useGetByIdVehicleRegistrationFormQuery(id, { skip: !id });

    const [deleteVehicleRegistrationForm, { isLoading: isDeleting }] = useDeleteVehicleRegistrationFormMutation();

    const [changeStatus, { isLoading: isChangingStatus }] = useChangeStatusVehicleRegistrationFormMutation();

    const {
        data: checkChassisData,
        isLoading,
        isError: checkChassisError
    } = useCheckChassisNoQuery(formData.chassisNo, {
        skip: !formData.chassisNo
    });

    const STATUS_CONFIG = {
        [CVRFStatus.Draft]: { label: th('warranty.dealerVehicleRegistrationForm.status.draft'), severity: 'info', icon: 'pi-file-edit' },
        [CVRFStatus.SentForApproval]: { label: th('warranty.dealerVehicleRegistrationForm.status.sentForApproval'), severity: 'warning', icon: 'pi-clock' },
        [CVRFStatus.Return]: { label: th('warranty.dealerVehicleRegistrationForm.status.return'), severity: 'danger', icon: 'pi-replay' },
        [CVRFStatus.Approved]: { label: th('warranty.dealerVehicleRegistrationForm.status.approved'), severity: 'success', icon: 'pi-check-circle' }
    };

    const currentStatus = formData?.status;
    const currentStatusConfig = STATUS_CONFIG[currentStatus];

    const normalizeFormData = (form) => {
        return {
            ...form,
            vehicleRegistration: formatDateForApi(form.vehicleRegistration)
        };
    };
    const hasUnsavedChanges = () => {
        const current = normalizeFormData(formData);
        const initial = normalizeFormData(initialFormData);
        return JSON.stringify(current) !== JSON.stringify(initial);
    };

    const [uploadDescription, setUploadDescription] = useState('');
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    useEffect(() => {
        const formIsReady = formData?.chassisNo && (!editMode || vehicleRegistrationFormData !== null);

        if (formIsReady && Object.keys(initialFormData).length === 0) {
            setInitialFormData(formData);
        }
    }, [formData, vehicleRegistrationFormData, editMode]);

    useEffect(() => {
        if (editMode && vehicleRegistrationFormData) {
            setFormData({
                id: vehicleRegistrationFormData.id,
                chassisNo: vehicleRegistrationFormData.chassisNo,
                vehiclePlate: vehicleRegistrationFormData.vehiclePlate,
                kmInformation: vehicleRegistrationFormData.kmInformation,
                vehicleRegistration: new Date(vehicleRegistrationFormData.vehicleRegistration),
                returnMsg: vehicleRegistrationFormData.returnMsg,
                nameCompany: vehicleRegistrationFormData.nameCompany,
                customerPhone: vehicleRegistrationFormData.customerPhone,
                customerEmail: vehicleRegistrationFormData.customerEmail,
                customerAddress: vehicleRegistrationFormData.customerAddress,
                customerStreet: vehicleRegistrationFormData.customerStreet,
                customerHome: vehicleRegistrationFormData.customerHome,
                customerCity: vehicleRegistrationFormData.customerCity,
                customerPostalCode: vehicleRegistrationFormData.customerPostalCode,
                customerCountry: vehicleRegistrationFormData.customerCountry,
                status: vehicleRegistrationFormData.status,
                comments: vehicleRegistrationFormData.comments || []
            });
        }
    }, [vehicleRegistrationFormData]);

    const [vinErrorMessage, setVinErrorMessage] = useState('');

    useEffect(() => {
        if (!formData.chassisNo) {
            setVinErrorMessage('');
            return;
        }

        if (checkChassisError) {
            setVinErrorMessage(th('warranty.dealerVehicleRegistrationForm.form.errors.vinCheckError'));
            return;
        }

        if (checkChassisData) {
            if (!checkChassisData.exists) {
                const message = checkChassisData.returnMessage || th('warranty.dealerVehicleRegistrationForm.form.errors.vinNotFound');
                setVinErrorMessage(message);
            } else {
                setVinErrorMessage('');
            }
        }
    }, [formData.chassisNo, checkChassisData, checkChassisError]);

    const getButtons = () => {
        const status = formData?.status;
        if (!editMode) {
            return [
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.save'),
                    onClick: handleSave,
                    severity: 'success'
                }
            ];
        }
        if (status === CVRFStatus.Draft) {
            return [
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.delete'),
                    severity: 'danger',
                    onClick: () => setShowDeleteConfirm(true),
                    disabled: isDeleting
                },
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.applyChanges'),
                    severity: 'gray',
                    onClick: handleSave
                },
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.sendForApproval'),
                    severity: 'warning',
                   onClick: () => setShowSendForApprovalDialog(true),
                    disabled: isChangingStatus
                }
            ];
        }
        if (status === CVRFStatus.Return) {
            return [
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.delete'),
                    severity: 'danger',
                    onClick: () => setShowDeleteConfirm(true),
                    disabled: isDeleting
                },
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.applyChanges'),
                    severity: 'gray',
                    onClick: handleSave
                },
                {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.sendForApproval'),
                    severity: 'warning',
                   onClick: () => setShowSendForApprovalDialog(true),
                    disabled: isChangingStatus
                }
            ];
        }
        if (status === CVRFStatus.SentForApproval) {
            return [
                authorizationCheckforVehicleRegistrationForm && {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.return'),
                    severity: 'danger',
                    onClick: () => setShowReturnDialog(true),
                    disabled: isChangingStatus
                },
                authorizationCheckforVehicleRegistrationForm && {
                    label: th('warranty.dealerVehicleRegistrationForm.buttons.approve'),
                    severity: 'success',
                    onClick: () => setShowApproveDialog(true),
                    disabled: isChangingStatus
                }
            ].filter(Boolean);
        }

        return [];
    };

    const handleSave = async () => {
        setSubmitted(true);
        if (getMissingRequiredFields(formData).length > 0) return;

        if (vinErrorMessage || !checkChassisData?.exists) {
            toastService.error(vinErrorMessage || th('warranty.dealerVehicleRegistrationForm.form.errors.vafCheckFailed'));
            return;
        }

        if (formData.customerEmail && !validateEmail(formData.customerEmail)) {
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.errors.invalidEmail'));
            return;
        }

        if (formData.customerPhone && !validatePhone(formData.customerPhone)) {
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.errors.invalidPhone'));
            return;
        }

        if (formData.customerPostalCode && !validatePostalCode(formData.customerPostalCode)) {
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.errors.invalidPostalCode'));
            return;
        }

        const success = await saveVehicleRegistrationForm(formData, editMode);

        if (success) {
            toastService.success(th('warranty.dealerVehicleRegistrationForm.form.toasts.changesSaved'));
            navigate(CVRF_LIST_ROUTE);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteVehicleRegistrationForm(formData.id).unwrap();

            toastService.success(th('warranty.dealerVehicleRegistrationForm.form.toasts.formDeleted'));
            navigate(CVRF_LIST_ROUTE);
        } catch (error) {
            console.error('Silme hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.toasts.deleteError'));
        }
    };

    const handleSubmitForApproval = async () => {
        const success = await saveVehicleRegistrationForm(formData, true);
        if (!success) return;

        try {
            const result = await changeStatus({
                id: formData.id,
                status: CVRFStatus.SentForApproval,
                returnMsg: sendForApprovalMessage  || ''
            }).unwrap();

            if (!result.isSuccessful) {
                toastService.error(result.errorMessage || th('warranty.dealerVehicleRegistrationForm.form.toasts.returnError'));
                return;
            }

            toastService.success(th('warranty.dealerVehicleRegistrationForm.form.toasts.sentForApproval'));
            navigate(CVRF_LIST_ROUTE);
        } catch (error) {
            console.error('Statü güncelleme hatası:', error);
        }
    };

    const handleReturn = async () => {
        if (!returnMessage) {
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.errors.returnReasonRequired'));
            return;
        }

        try {
            const result = await changeStatus({
                id: formData.id,
                status: CVRFStatus.Return,
                returnMsg: returnMessage
            }).unwrap();

            if (!result.isSuccessful) {
                toastService.error(result.errorMessage || th('warranty.dealerVehicleRegistrationForm.form.toasts.returnError'));
                return;
            }

            toastService.success(th('warranty.dealerVehicleRegistrationForm.form.toasts.formReturned'));
            navigate(CVRF_LIST_ROUTE);
        } catch (error) {
            console.error('Geri gönderme hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.toasts.returnError'));
        }
    };

    const handleApprove = async () => {
        try {
            const result = await changeStatus({
                id: formData.id,
                status: CVRFStatus.Approved,
                returnMsg: approveMessage || ''
            }).unwrap();

            if (!result.isSuccessful) {
                toastService.error(result.errorMessage || th('warranty.dealerVehicleRegistrationForm.form.toasts.returnError'));
                return;
            }

            toastService.success(th('warranty.dealerVehicleRegistrationForm.form.toasts.formApproved'));
            navigate(CVRF_LIST_ROUTE);
        } catch (error) {
            console.error('Onaylama hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.form.toasts.approveError'));
        }
    };

    const handleExit = () => {
        if (editMode) {
            if (hasUnsavedChanges()) {
                confirmDialog({
                    message: th('warranty.dealerVehicleRegistrationForm.form.confirmations.unsavedChanges'),
                    header: th('warranty.dealerVehicleRegistrationForm.form.confirmations.warningHeader'),
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: th('warranty.dealerVehicleRegistrationForm.buttons.yes'),
                    rejectLabel: th('warranty.dealerVehicleRegistrationForm.buttons.no'),
                    accept: () => navigate(CVRF_LIST_ROUTE),
                    rejectClassName: 'p-button-outlined'
                });
            } else {
                navigate(CVRF_LIST_ROUTE);
            }
        } else {
            navigate(CVRF_LIST_ROUTE);
        }
    };

    return (
        <div className="p-fluid">
            {editMode && currentStatusConfig && (
                <div className="flex justify-content-end">
                    <Tag value={currentStatusConfig.label} severity={currentStatusConfig.severity} icon={`pi ${currentStatusConfig.icon}`} />
                </div>
            )}

            {editMode && currentStatus === CVRFStatus.Return && <Message severity="info" text={th('warranty.dealerVehicleRegistrationForm.form.messages.formReturned')} className="w-full mb-4" icon="pi pi-info-circle" />}

            {editMode && currentStatus === CVRFStatus.Approved && <Message severity="success" text={th('warranty.dealerVehicleRegistrationForm.form.messages.formApproved')} className="w-full mb-4" icon="pi pi-check-circle" />}

            {vinErrorMessage && <Message severity="error" text={vinErrorMessage} className="w-full mb-4" icon="pi pi-times-circle" />}

            <div className="grid">
                <div className="col-6 grid">
                    <h3 className="section-title">
                        <i className="pi pi-car mr-2"></i>
                        {th('warranty.dealerVehicleRegistrationForm.form.sections.vehicleInfo')}
                    </h3>
                    <Divider className="m-1" />
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="chassisNo" className="font-bold block mb-2">
                                <i className="pi pi-barcode mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.vin')} *
                            </label>
                            <ChassisDropdown
                                useChassisQuery={useGetChassisListForCVRFQuery}
                                placeholderKey="warranty.dealerVehicleRegistrationForm.form.placeholders.vinSearch"
                                disabled={!isFormEditable}
                                value={formData.chassisNo}
                                onChange={(value) => handleChange('chassisNo', value)}
                                className={(submitted && !formData.chassisNo) || vinErrorMessage ? 'p-invalid' : ''}
                            />
                            {submitted && !formData.chassisNo && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.vinRequired')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="vehiclePlate" className="font-bold block mb-2">
                                <i className="pi pi-id-card mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.plateNo')} *
                            </label>
                            <InputText
                                disabled={!isFormEditable}
                                value={formData.vehiclePlate}
                                onChange={(e) => handleChange('vehiclePlate', e.target.value)}
                                className={submitted && !formData.vehiclePlate ? 'p-invalid' : ''}
                                placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.plateNo')}
                            />
                            {submitted && !formData.vehiclePlate && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.plateNoRequired')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="kmInformation" className="font-bold block mb-2">
                                <i className="pi pi-map-marker mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.mileage')} *
                            </label>
                            <InputText
                                disabled={!isFormEditable}
                                value={formData.kmInformation}
                                onChange={(e) => handleChange('kmInformation', e.target.value)}
                                className={submitted && !formData.kmInformation ? 'p-invalid' : ''}
                                placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.mileage')}
                            />
                            {submitted && !formData.kmInformation && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.mileageRequired')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="vehicleRegistration" className="font-bold block mb-2">
                                <i className="pi pi-calendar mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.registrationDate')} *
                            </label>
                            {editMode && formData?.status === CVRFStatus.Approved ? (
                                <DateChangeRequestField
                                    formType={FORM_TYPE.CVRF}
                                    dateField={DATE_FIELD.VEHICLE_REGISTRATION}
                                    formId={formData.id}
                                    currentDate={formData.vehicleRegistration}
                                    label={th('warranty.dealerVehicleRegistrationForm.form.labels.registrationDate')}
                                />
                            ) : (
                                <>
                                    <Calendar
                                        disabled={!isFormEditable}
                                        id="vehicleRegistration"
                                        value={formData.vehicleRegistration}
                                        onChange={(e) => handleChange('vehicleRegistration', e.value)}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        className={submitted && !formData.vehicleRegistration ? 'p-invalid' : ''}
                                        placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.date')}
                                    />
                                    {submitted && !formData.vehicleRegistration && (
                                        <small className="p-error">
                                            <i className="pi pi-exclamation-circle mr-1"></i>
                                            {th('warranty.dealerVehicleRegistrationForm.form.errors.dateRequired')}
                                        </small>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-6 grid">
                    <h3 className="section-title">
                        <i className="pi pi-user mr-2"></i>
                        {th('warranty.dealerVehicleRegistrationForm.form.sections.customerInfo')}
                    </h3>
                    <Divider className="m-1" />

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="nameCompany" className="font-bold block mb-2">
                                <i className="pi pi-building mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.nameCompany')} *
                            </label>
                            <InputText
                                disabled={!isFormEditable}
                                value={formData.nameCompany}
                                onChange={(e) => handleChange('nameCompany', e.target.value)}
                                className={submitted && !formData.nameCompany ? 'p-invalid' : ''}
                                rows={2}
                                placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.nameCompany')}
                            />
                            {submitted && !formData.nameCompany && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.nameCompanyRequired')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="customerEmail" className="font-bold block mb-2">
                                <i className="pi pi-envelope mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.email')} *
                            </label>
                            <InputText
                                type="email"
                                disabled={!isFormEditable}
                                value={formData.customerEmail}
                                onChange={(e) => handleChange('customerEmail', e.target.value)}
                                className={submitted && (!formData.customerEmail || !validateEmail(formData.customerEmail)) ? 'p-invalid' : ''}
                                placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.email')}
                            />
                            {submitted && !formData.customerEmail && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.emailRequired')}
                                </small>
                            )}
                            {submitted && formData.customerEmail && !validateEmail(formData.customerEmail) && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.invalidEmail')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="customerPhone" className="font-bold block mb-2">
                                <i className="pi pi-phone mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.phone')} *
                            </label>
                            <InputText
                                type="tel"
                                disabled={!isFormEditable}
                                value={formData.customerPhone}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, '');
                                    handleChange('customerPhone', numericValue);
                                }}
                                className={submitted && (!formData.customerPhone || !validatePhone(formData.customerPhone)) ? 'p-invalid' : ''}
                                placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.phone')}
                            />
                            {submitted && !formData.customerPhone && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.phoneRequired')}
                                </small>
                            )}
                            {submitted && formData.customerPhone && !validatePhone(formData.customerPhone) && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.invalidPhone')}
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="customerCountry" className="font-bold block mb-2">
                                <i className="pi pi-globe mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.labels.country')} *
                            </label>
                            <CountryDropdown value={formData.customerCountry} onChange={(val) => handleChange('customerCountry', val)} className={submitted && !formData.customerCountry ? 'p-invalid w-full' : 'w-full'} disabled={!isFormEditable} />
                            {submitted && !formData.customerCountry && (
                                <small className="p-error">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.errors.countryRequired')}
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <h3 className="section-title">
                        <i className="pi pi-map mr-2"></i>
                        {th('warranty.dealerVehicleRegistrationForm.form.sections.addressInfo')}
                    </h3>
                    <Divider />
                </div>

                <div className="col-12 md:col-2">
                    <div className="field mb-3">
                        <label htmlFor="customerCity" className="font-bold block mb-2">
                            <i className="pi pi-building mr-1"></i>
                            {th('warranty.dealerVehicleRegistrationForm.form.labels.city')} *
                        </label>
                        <InputText
                            disabled={!isFormEditable}
                            value={formData.customerCity}
                            onChange={(e) => handleChange('customerCity', e.target.value)}
                            className={submitted && !formData.customerCity ? 'p-invalid' : ''}
                            placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.city')}
                        />
                        {submitted && !formData.customerCity && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.cityRequired')}
                            </small>
                        )}
                    </div>
                </div>

                <div className="col-12 md:col-2">
                    <div className="field mb-3">
                        <label htmlFor="customerStreet" className="font-bold block mb-2">
                            <i className="pi pi-map-marker mr-1"></i>
                            {th('warranty.dealerVehicleRegistrationForm.form.labels.street')} *
                        </label>
                        <InputText
                            disabled={!isFormEditable}
                            value={formData.customerStreet}
                            onChange={(e) => handleChange('customerStreet', e.target.value)}
                            className={submitted && !formData.customerStreet ? 'p-invalid' : ''}
                            rows={2}
                            placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.street')}
                        />
                        {submitted && !formData.customerStreet && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.streetRequired')}
                            </small>
                        )}
                    </div>
                </div>

                <div className="col-12 md:col-2">
                    <div className="field mb-3">
                        <label htmlFor="customerHome" className="font-bold block mb-2">
                            <i className="pi pi-hashtag mr-1"></i>
                            {th('warranty.dealerVehicleRegistrationForm.form.labels.homeNo')} *
                        </label>
                        <InputText
                            disabled={!isFormEditable}
                            value={formData.customerHome}
                            onChange={(e) => handleChange('customerHome', e.target.value)}
                            className={submitted && !formData.customerHome ? 'p-invalid' : ''}
                            placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.homeNo')}
                        />
                        {submitted && !formData.customerHome && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.homeNoRequired')}
                            </small>
                        )}
                    </div>
                </div>

                <div className="col-12 md:col-2">
                    <div className="field mb-3">
                        <label htmlFor="customerPostalCode" className="font-bold block mb-2">
                            <i className="pi pi-envelope mr-1"></i>
                            {th('warranty.dealerVehicleRegistrationForm.form.labels.postalCode')} *
                        </label>
                        <InputText
                            disabled={!isFormEditable}
                            value={formData.customerPostalCode}
                            onChange={(e) => handleChange('customerPostalCode', e.target.value)}
                            className={submitted && (!formData.customerPostalCode || !validatePostalCode(formData.customerPostalCode)) ? 'p-invalid' : ''}
                            placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.postalCode')}
                            maxLength={5}
                        />
                        {submitted && !formData.customerPostalCode && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.postalCodeRequired')}
                            </small>
                        )}
                        {submitted && formData.customerPostalCode && !validatePostalCode(formData.customerPostalCode) && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.invalidPostalCode')}
                            </small>
                        )}
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-3">
                        <label htmlFor="customerAddress" className="font-bold block mb-2">
                            <i className="pi pi-home mr-1"></i>
                            {th('warranty.dealerVehicleRegistrationForm.form.labels.address')} *
                        </label>
                        <InputTextarea
                            disabled={!isFormEditable}
                            value={formData.customerAddress}
                            onChange={(e) => handleChange('customerAddress', e.target.value)}
                            className={submitted && !formData.customerAddress ? 'p-invalid' : ''}
                            rows={1}
                            placeholder={th('warranty.dealerVehicleRegistrationForm.form.placeholders.address')}
                        />
                        {submitted && !formData.customerAddress && (
                            <small className="p-error">
                                <i className="pi pi-exclamation-circle mr-1"></i>
                                {th('warranty.dealerVehicleRegistrationForm.form.errors.addressRequired')}
                            </small>
                        )}
                    </div>
                </div>

                {formData.returnMsg && (
                    <>
                        <div className="col-12">
                            <Divider />
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-info-circle mr-1"></i>
                                    {th('warranty.dealerVehicleRegistrationForm.form.labels.message')}
                                </label>
                                <div className="p-3 border-round surface-100 border-1 surface-border">
                                    <p className="m-0 text-color-secondary">
                                        <i className="pi pi-quote-left mr-2"></i>
                                        {formData.returnMsg}
                                        <i className="pi pi-quote-right ml-2"></i>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {formData?.comments && (
                    <div className="col-12">
                        <ApprovalCommentsTable data={formData.comments} isLoading={isLoadingVehicleRegistrationForm} />
                    </div>
                )}

                <div className="col-12 md:col-12" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className="col-1" style={{ height: '3.5rem' }}>
                        <Button
                            label={th('warranty.dealerVehicleRegistrationForm.buttons.exit')}
                            severity="warning"
                            onClick={handleExit}
                            className="p-button"
                            style={{
                                minWidth: '2rem',
                                fontSize: '0.75rem',
                                color: 'white',
                                fontWeight: 'bold',
                                backgroundColor: 'gray',
                                borderColor: 'gray',
                                padding: '0.5rem 1rem',
                                height: '100%'
                            }}
                        />
                    </div>

                    <div className="col-5" style={{ height: '3rem' }}>
                        <ButtonGroup buttons={getButtons()} />
                    </div>
                </div>
            </div>

            <ConfirmDialog
                visible={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                message={th('warranty.dealerVehicleRegistrationForm.form.confirmations.deleteMessage')}
                header={th('warranty.dealerVehicleRegistrationForm.form.confirmations.deleteHeader')}
                icon="pi pi-exclamation-triangle"
                accept={handleDelete}
                reject={() => setShowDeleteConfirm(false)}
            />

            <CVRFCommentConfirmDialog
                visible={showSendForApprovalDialog}
                onHide={() => {
                    setShowSendForApprovalDialog(false);
                    setSendForApprovalMessage('');
                }}
                header={th('warranty.dealerVehicleRegistrationForm.form.confirmations.sendForApprovalHeader') || 'Onaya Gönder'}
                promptText={th('warranty.dealerVehicleRegistrationForm.form.confirmations.sendForApprovalMessage') || 'İsteğe bağlı bir yorum ekleyebilirsiniz:'}
                value={sendForApprovalMessage}
                onChange={(e) => setSendForApprovalMessage(e.target.value)}
                placeholder={th('warranty.dealerVehicleRegistrationForm.form.confirmations.optionalComment') || 'Yorumunuz (isteğe bağlı)'}
                accept={handleSubmitForApproval}
                reject={() => {
                    setShowSendForApprovalDialog(false);
                    setSendForApprovalMessage('');
                }}
                acceptLabel={th('warranty.dealerVehicleRegistrationForm.buttons.sendForApproval') || 'Gönder'}
                rejectLabel={th('warranty.dealerVehicleRegistrationForm.buttons.cancel')}
            />

            <CVRFCommentConfirmDialog
                visible={showReturnDialog}
                onHide={() => setShowReturnDialog(false)}
                header={th('warranty.dealerVehicleRegistrationForm.form.confirmations.returnHeader')}
                promptText={th('warranty.dealerVehicleRegistrationForm.form.confirmations.returnMessage')}
                value={returnMessage}
                onChange={(e) => setReturnMessage(e.target.value)}
                required
                accept={handleReturn}
                reject={() => setShowReturnDialog(false)}
                acceptLabel={th('warranty.dealerVehicleRegistrationForm.buttons.return')}
                rejectLabel={th('warranty.dealerVehicleRegistrationForm.buttons.cancel')}
            />

            <CVRFCommentConfirmDialog
                visible={showApproveDialog}
                onHide={() => {
                    setShowApproveDialog(false);
                    setApproveMessage('');
                }}
                header={th('warranty.dealerVehicleRegistrationForm.form.confirmations.approveHeader') || 'Onayla'}
                promptText={th('warranty.dealerVehicleRegistrationForm.form.confirmations.approveMessage') || 'İsteğe bağlı bir yorum ekleyebilirsiniz:'}
                value={approveMessage}
                onChange={(e) => setApproveMessage(e.target.value)}
                placeholder={th('warranty.dealerVehicleRegistrationForm.form.confirmations.optionalComment') || 'Yorumunuz (isteğe bağlı)'}
                accept={handleApprove}
                reject={() => {
                    setShowApproveDialog(false);
                    setApproveMessage('');
                }}
                acceptLabel={th('warranty.dealerVehicleRegistrationForm.buttons.approve')}
                rejectLabel={th('warranty.dealerVehicleRegistrationForm.buttons.cancel')}
            />
        </div>
        
    );
};

export default FormVehicleRegistrationForm;
